import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
const DEFAULT_POLL_MS = 4000;
/**
 * KTK-335 — first-run "Connect your runtime" panel. The day-one path for a
 * fresh user: install the Kritaka Agent Runtime on the machine they want
 * agents on, sign in there with the same account, and this panel detects
 * the runtime registering and hands them to creating their first agent.
 *
 * One runtime, both kinds: the connected runtime drives Claude Code AND V2
 * agents — there is no separate daemon in the user's path.
 *
 * Liveness seam: presence in `listAgentRuntimeHosts()` = registered (what we
 * show today). `last_seen_at` becomes the live signal once the V2
 * relay-liveness lane (rocky, KTK-339) stamps it on connect — no change
 * needed here, the field is already read through.
 */
export function ConnectRuntimePanel({ hub, onCreateAgent, installUrl, pollIntervalMs = DEFAULT_POLL_MS, onSkip, }) {
    const [hosts, setHosts] = useState(null);
    const [pollError, setPollError] = useState(null);
    // Keep the latest hub without re-arming the poll loop when its identity
    // shifts (DOM-state stability — don't tear down the interval mid-wait).
    const hubRef = useRef(hub);
    hubRef.current = hub;
    useEffect(() => {
        let cancelled = false;
        let timer = null;
        const poll = async () => {
            try {
                const list = await hubRef.current.listAgentRuntimeHosts();
                if (cancelled)
                    return;
                setHosts(list);
                setPollError(null);
            }
            catch (e) {
                if (cancelled)
                    return;
                // Keep waiting on transient hub errors — the runtime may simply not
                // be up yet. Surface the message quietly without dropping the loop.
                setPollError(e instanceof Error ? e.message : String(e));
            }
            finally {
                if (!cancelled)
                    timer = setTimeout(poll, pollIntervalMs);
            }
        };
        void poll();
        return () => {
            cancelled = true;
            if (timer)
                clearTimeout(timer);
        };
    }, [pollIntervalMs]);
    const connected = (hosts?.length ?? 0) > 0;
    return (_jsx(PageShell, { children: connected ? (_jsx(ConnectedState, { hosts: hosts, onCreateAgent: onCreateAgent })) : (_jsx(WaitingState, { installUrl: installUrl, pollError: pollError, onSkip: onSkip })) }));
}
function WaitingState({ installUrl, pollError, onSkip, }) {
    return (_jsxs(_Fragment, { children: [_jsxs("div", { children: [_jsx("h2", { style: titleStyle, children: "Connect your runtime" }), _jsx("p", { style: hintStyle, children: "The Kritaka Agent Runtime runs your agents \u2014 Claude Code or the newer kind \u2014 on your own machines. Install it once, sign in, and you're set." })] }), _jsxs("ol", { style: stepListStyle, children: [_jsxs("li", { style: stepStyle, children: [_jsx("strong", { children: "Install" }), " the Kritaka Agent Runtime on the computer you want your agents to run on.", _jsx("div", { style: { marginTop: 8 }, children: installUrl ? (_jsx("a", { href: installUrl, style: primaryLinkBtn, children: "Download for your machine" })) : (_jsx("span", { style: comingSoonBtn, "aria-disabled": true, children: "Installers coming soon" })) })] }), _jsxs("li", { style: stepStyle, children: [_jsx("strong", { children: "Sign in" }), " in the runtime app with this same account."] })] }), _jsxs("div", { style: waitingRow, children: [_jsx(Spinner, {}), _jsx("span", { children: "Waiting for your runtime to connect\u2026" })] }), pollError && (_jsx("div", { style: mutedNote, children: "Couldn't reach the hub just now \u2014 still trying." })), onSkip && (_jsx("button", { type: "button", onClick: onSkip, style: skipLink, children: "Skip for now" }))] }));
}
function ConnectedState({ hosts, onCreateAgent, }) {
    return (_jsxs(_Fragment, { children: [_jsxs("div", { children: [_jsx("h2", { style: { ...titleStyle, color: "var(--success)" }, children: "Runtime connected" }), _jsx("p", { style: hintStyle, children: "You're set up. Your runtime is ready to run agents \u2014 Claude Code and the newer kind, all through this one install." })] }), _jsx("div", { style: hostListStyle, children: hosts.map((h) => (_jsxs("div", { style: hostRowStyle, children: [_jsx("span", { style: hostDot }), _jsxs("div", { style: { minWidth: 0 }, children: [_jsx("div", { style: hostName, children: h.display_name }), _jsxs("div", { style: hostMeta, children: [platformLabel(h.platform), h.hostname ? ` · ${h.hostname}` : ""] }), h.claude_logged_in === false && (_jsx("div", { style: hostWarning, children: "Claude isn't signed in on this machine \u2014 sign into Claude there so your agents can run." }))] })] }, h.host_id))) }), _jsx("div", { style: { display: "flex", justifyContent: "flex-end" }, children: _jsx("button", { type: "button", onClick: onCreateAgent, style: primaryBtn, children: "Create your first agent" }) })] }));
}
function Spinner() {
    return (_jsx("span", { style: {
            width: 14,
            height: 14,
            border: "2px solid var(--border)",
            borderTopColor: "var(--accent)",
            borderRadius: "50%",
            display: "inline-block",
            animation: "ktk-spin 0.8s linear infinite",
        } }));
}
function PageShell({ children }) {
    return (_jsxs("div", { className: "app-pre-shell", style: {
            minHeight: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "var(--bg-primary)",
            padding: 24,
        }, children: [_jsx("style", { children: "@keyframes ktk-spin{to{transform:rotate(360deg)}}" }), _jsxs("div", { style: {
                    width: "100%",
                    maxWidth: 480,
                    background: "var(--bg-secondary)",
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    padding: 28,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    boxShadow: "0 16px 40px rgba(0,0,0,0.35)",
                }, children: [_jsx("div", { style: { fontSize: 12, color: "var(--text-muted)", letterSpacing: 1 }, children: "KRITAKA \u00B7 GET STARTED" }), children] })] }));
}
function platformLabel(p) {
    if (p === "darwin")
        return "macOS";
    if (p === "win32")
        return "Windows";
    if (p === "linux")
        return "Linux";
    return p;
}
const titleStyle = {
    margin: 0,
    fontSize: 18,
    color: "var(--text-primary)",
};
const hintStyle = {
    fontSize: 13,
    color: "var(--text-secondary)",
    margin: "8px 0 0",
    lineHeight: 1.5,
};
const stepListStyle = {
    margin: 0,
    paddingLeft: 20,
    display: "flex",
    flexDirection: "column",
    gap: 14,
};
const stepStyle = {
    fontSize: 13,
    color: "var(--text-secondary)",
    lineHeight: 1.5,
};
const waitingRow = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 13,
    color: "var(--text-muted)",
};
const mutedNote = {
    fontSize: 12,
    color: "var(--text-muted)",
};
const skipLink = {
    alignSelf: "flex-start",
    background: "none",
    border: "none",
    padding: 0,
    fontSize: 12,
    color: "var(--text-muted)",
    cursor: "pointer",
    fontFamily: "inherit",
    textDecoration: "underline",
};
const hostListStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 8,
};
const hostRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    background: "var(--bg-tertiary)",
    border: "1px solid var(--border)",
    borderRadius: 8,
};
const hostDot = {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "var(--success)",
    flexShrink: 0,
};
const hostName = {
    fontSize: 14,
    color: "var(--text-primary)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
};
const hostMeta = {
    fontSize: 12,
    color: "var(--text-muted)",
};
const hostWarning = {
    marginTop: 4,
    fontSize: 12,
    color: "var(--error)",
    lineHeight: 1.4,
};
const primaryBtn = {
    padding: "8px 18px",
    fontSize: 13,
    fontWeight: 500,
    border: "none",
    borderRadius: 6,
    color: "white",
    background: "var(--accent)",
    cursor: "pointer",
    fontFamily: "inherit",
};
const primaryLinkBtn = {
    ...primaryBtn,
    display: "inline-block",
    textDecoration: "none",
};
const comingSoonBtn = {
    ...primaryBtn,
    background: "var(--bg-hover)",
    color: "var(--text-muted)",
    cursor: "not-allowed",
};
//# sourceMappingURL=ConnectRuntimePanel.js.map